import {} from 'react';
import { Route, Redirect, useLocation } from 'react-router-dom';
import { isAuthenticated, checkRole, userProfile, getDefaultNavigationLink } from 'utils/auth';
import { useSelector } from 'react-redux';
import { NotAuthorizedPage } from 'app/components/NotAuthorizedPage/Loadable';
import { selectDrawerOpen } from 'app/pages/Header/selectors';
import { Header } from 'app/pages/Header/Loadable';
import { Sidebar } from 'app/pages/Sidebar/Loadable';
import { ComponentPageContainer } from 'app/components/StyledComponents';
import { Box } from '@mui/material';
import { shouldRestrictToAILayout } from './AIAvatarRoute';

export const AuthorizedRoute = ({ component, roles, ...rest }: any) => {
  const isDrawerOpen = useSelector(selectDrawerOpen) as boolean;
  const user = userProfile();
  // const hasAccess = roles ? checkRole(user?.roles || [], roles) : true;
  const hasAccess = true;

  if (!user) {
    return <Redirect to="/login" />;
  }

  // Check old_layout flag from user token
  // If old_layout is false, user should use NewAIAvatarLayout
  const oldLayout = user?.old_layout !== undefined ? user.old_layout : true; // Default to true if not present
  if (oldLayout === false) {
    // User should use NewAIAvatarLayout, redirect to AI Avatar Layout
    // Get default navigation link or fallback to /ai-avatar-layout
    const defaultNavLink = getDefaultNavigationLink();
    const redirectPath = defaultNavLink || '/ai-avatar-layout';
    return <Redirect to={redirectPath} />;
  }

  // Check if user should be restricted to AI Avatar Layout only (legacy role-based check)
  const userRoles = user?.roles || [];
  const restrictedToAILayout = shouldRestrictToAILayout(userRoles);
  
  // If user is NewAIAvatarLayout role and trying to access other routes, redirect to AI layout
  if (restrictedToAILayout && !rest.path?.startsWith('/ai-avatar-layout')) {
    return <Redirect to="/ai-avatar-layout" />;
  }

  if (!hasAccess) {
    return (
      <>
      <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>

        <Header />
        <Box sx={{display: 'flex', flexDirection: 'row', flex: 1}}>
          <Sidebar />
          <ComponentPageContainer
            style={{ 
              marginLeft: '64px',
              transition: 'margin-left 0.2s ease-in-out'
            }}
          >
            <Route component={NotAuthorizedPage} {...rest} />
          </ComponentPageContainer>
        </Box>
      </Box>
      </>
    );
  } else
    return (
      <>
        <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
          <Header />
          <Box sx={{display: 'flex', flexDirection: 'row', width: '100%'}}>
            <Sidebar />
            <ComponentPageContainer
              style={{ 
                marginLeft: '64px',
                transition: 'margin-left 0.2s ease-in-out',
                width: 'calc(100% - 64px)'
              }}
            >
              <Route component={component} {...rest} />
            </ComponentPageContainer>
          </Box>
        </Box>
      </>
    );
};

export const UnAuthorizedRoute = ({ component, ...rest }: any) => {
  // if (!isAuthenticated()) {
  return <Route component={component} {...rest} />;
  // }
  return <Redirect push to="/" />;
};

// New FlexibleRoute component that works with or without authentication
export const FlexibleRoute = ({ component, roles, ...rest }: any) => {
  const isDrawerOpen = useSelector(selectDrawerOpen) as boolean;
  const location = useLocation();
  const user = userProfile(false); // Don't redirect automatically
  const isLoggedIn = isAuthenticated(false); // Don't redirect automatically
  
  // Check if current path is ShareableNurturingHistoryPage and theme is light/dark
  // In that case, let the component handle its own layout
  const isShareableNurturingHistoryPage = location.pathname.startsWith('/url/');
  const currentTheme = localStorage.getItem('app-theme') || 'blue';
  
  // Check if user has NewAIAvatarLayout roles
  const userRoles = user?.roles || [];
  const isBetaPerformanceManager = userRoles.includes('beta_maiq_performance_manager');
  const isAiMaiqSalesManager = userRoles.includes('ai_maiq_sales');
  const isDealershipExecutive = userRoles.includes('dealership_executive');
  const isDealershipGroupExecutive = userRoles.includes('dealership_group_executive');
  const isPerformanceManager = userRoles.includes('maiq_performance_manager');
  const isContentManager = userRoles.includes('maiq_content_manager');
  
  // NewAIAvatarLayout users can use AI Avatar Layout with light/dark theme
  const useAIAvatarLayout = isLoggedIn && (isBetaPerformanceManager || isDealershipExecutive || isDealershipGroupExecutive || isPerformanceManager || isContentManager || isAiMaiqSalesManager) && ['light', 'dark'].includes(currentTheme) && isShareableNurturingHistoryPage;
  
  // If using AI Avatar Layout (ShareableNurturingHistoryPage with light/dark theme), skip default wrapper
  if (useAIAvatarLayout) {
    return <Route component={component} {...rest} />;
  }
  
  // If user is logged in, render with sidebar and header
  if (isLoggedIn && user && Object.keys(user).length) {
    const hasAccess = roles ? checkRole(user?.roles || [], roles) : true;
    
    // Note: FlexibleRoute allows access from all contexts including AI Avatar Layout
    // No restrictions based on user roles for shareable pages
    
    if (!hasAccess) {
      return (
        <>
        <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
          <Header />
          <Box sx={{display: 'flex', flexDirection: 'row', flex: 1}}>
            <Sidebar />
            <ComponentPageContainer
              style={{ 
                marginLeft: '64px',
                transition: 'margin-left 0.2s ease-in-out'
              }}
            >
              <Route component={NotAuthorizedPage} {...rest} />
            </ComponentPageContainer>
          </Box>
        </Box>
        </>
      );
    }
    
    return (
      <>
        <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
          <Header />
          <Box sx={{display: 'flex', flexDirection: 'row', width: '100%'}}>
            <Sidebar />
            <ComponentPageContainer
              style={{ 
                marginLeft: '64px',
                transition: 'margin-left 0.2s ease-in-out',
                width: 'calc(100% - 64px)'
              }}
            >
              <Route component={component} {...rest} />
            </ComponentPageContainer>
          </Box>
        </Box>
      </>
    );
  }
  
  // If user is not logged in, render without sidebar and header (like UnAuthorizedRoute)
  return <Route component={component} {...rest} />;
};

export const MyautoiqRoute = ({ component, ...rest }: any) => {
  let pathName = window.location.pathname.split('/')[1];

  if (pathName === 'myautoiq') {
    return (
      <>
        <Route component={component} {...rest} />
      </>
    );
  }
  return <Redirect push to={'/login?redirect=' + window.location.pathname} />;
};
